(() => {
  /**
   * Handle the pot meters (mini, minor etc.) and their values
   */
  class PotMeters extends PIXI.Container {
    constructor() {
      super();
      this._meterNames = ["grand", "major", "minor", "mini"];

      // Pot meter assets
      this._potMeterAssets = {};

      this._awardPotAnimationTimeline = null;
      this._awardPotResetAnimationTimeline = null;

      this._awardPotTimelines = {
        grand: null,
        major: null,
        minor: null,
        mini: null,
      };

      this._awardPotResetTimelines = {
        grand: null,
        major: null,
        minor: null,
        mini: null,
      };

      this._awardPotDelayedCalls = {
        grand: [],
        major: [],
        minor: [],
        mini: [],
      };

      this._awardPotPromises = {
        grand: null,
        major: null,
        minor: null,
        mini: null,
      };

      this.awardingPotPromise = null;

      Reflex.registerEventHandler("orientationChange", this._orientationChange.bind(this));
    }

    /**
     * Initialise the class
     */
    initialise() {
      this._potMeterAssets = this._meterNames.reduce((acc, meterName) => {
        const meterAsset = resourceManager.get(`${this.name}:${meterName}Meter`);
        const textAsset = resourceManager.get(`${this.name}:${meterName}Meter:${meterName}MeterText`);

        const meterFlameAsset = resourceManager.createObject(
          "movie",
          `flames:${meterName}`,
          `potMetersContainer:${meterName}MeterFlames`,
          {
            definition: "potFlames",
            anchor: 0.5,
            loop: true,
          }
        );
        meterFlameAsset.alpha = 0;
        meterFlameAsset.gotoAndStop(0);

        const meterWinnerAsset = resourceManager.createObject(
          "movie",
          `winner:${meterName}`,
          `potMetersContainer:${meterName}Meter:${meterName}MeterWinner`,
          {
            definition: "potWinner",
            anchor: 0.5,
            loop: true,
          }
        );
        meterWinnerAsset.alpha = 0;
        meterWinnerAsset.gotoAndStop(0);

        acc[meterName] = {};
        acc[meterName]["asset"] = meterAsset;
        acc[meterName]["text"] = textAsset;
        acc[meterName]["flames"] = meterFlameAsset;
        acc[meterName]["winner"] = meterWinnerAsset;

        return acc;
      }, {});

      this.updateMeterValues();
    }

    /**
     * Update the meter values
     */
    updateMeterValues() {
      const inFS = gameController.boughtBonus || gameController.inFreeSpins;
      const inBonusBuyRecovery = gameController.inBuyABonusRecovery;
      const stake =
        inFS || inBonusBuyRecovery ? gameController.getCurrentModeStake() : gameController.globalHelperGetBaseStake();
      const potMeterValues = gameData.slotConfig.potMeters;
      for (const meterName in this._potMeterAssets) {
        const potValue = potMeterValues[meterName] / 10;
        this._potMeterAssets[meterName]["text"].setText(gdk.formatCurrency(stake * potValue));
      }
    }

    /**
     * Little heloper method for storing delayed calls we can easy kill if awarding same pot 1 after another
     * @param {*} meterName
     * @param {*} seconds
     */
    _wait(meterName, seconds) {
      return new Promise((res) => {
        const call = gsap.delayedCall(seconds, res);
        this._awardPotDelayedCalls[meterName].push(call);
      });
    }

    /**
     * Handle awarding a particular pot
     */
    async awardPot(meterName) {
      let currentAwardResolve = null;
      this.awardingPotPromise = new Promise((res) => {
        currentAwardResolve = res;
      });

      if (this._awardPotTimelines[meterName]) {
        this._awardPotTimelines[meterName].kill();
        this._awardPotTimelines[meterName] = null;
      }

      if (this._awardPotResetTimelines[meterName]) {
        this._awardPotResetTimelines[meterName].kill();
        this._awardPotResetTimelines[meterName] = null;
      }

      this._awardPotDelayedCalls[meterName].forEach((call) => call.kill());
      this._awardPotDelayedCalls[meterName].length = 0;

      const potAssets = this._potMeterAssets[meterName];
      const { text, flames, winner } = potAssets;

      const textScale = text.scale;

      // SET INITIAL VALUES
      text.alpha = 0;
      flames.alpha = 0;
      flames.scale.set(1);
      winner.alpha = 0;
      winner.scale.set(1);

      this._currentlyAwarding = true;

      flames.play();
      gsap.to(flames, { alpha: 1, duration: 0.1 });

      soundManager.play(`pot${meterName}`);

      // play pot animation
      await this.playPotAnimation(meterName, text, winner);

      // wait 1 sec
      await this._wait(meterName, 1);

      // play reset pot animation
      await this.resetPotAnimation(meterName, text, winner, textScale);

      gsap.to(flames, { alpha: 0, duration: 0.1 }).then(() => {
        flames.gotoAndStop(0);
        this._currentlyAwarding = false;
        currentAwardResolve();
        this.awardingPotPromise = null;
      });
    }

    /**
     * Play pot animation
     */
    async playPotAnimation(meterName, text, winner) {
      await new Promise((resolve) => {
        this._awardPotTimelines[meterName] = gsap
          .timeline({ onComplete: resolve })
          .to(text.scale, { x: 2, y: 2, duration: 0.3 })
          .to(text, { alpha: 0, duration: 0.3 }, "-=0.15")
          .call(
            () => {
              winner.alpha = 1;
              winner.play();
            },
            null,
            "<"
          )
          .fromTo(winner.scale, { x: 0.3, y: 0.3 }, { x: 1.2, y: 1.2, duration: 0.25 }, "<")
          .to(winner.scale, { x: 1, y: 1, duration: 0.1 });
      });
    }

    /**
     * Reset pot animation
     * @param {} param0
     */
    async resetPotAnimation(meterName, text, winner, textScale) {
      await new Promise((resolve) => {
        this._awardPotResetTimelines[meterName] = gsap
          .timeline({ onComplete: resolve })
          .fromTo(winner.scale, { x: 1, y: 1 }, { x: 1.2, y: 1.2, duration: 0.1 })
          .to(winner, { alpha: 0, duration: 0.1 }, "<")
          .to(text.scale, { x: textScale.lastScaleX, y: textScale.lastScaleY, duration: 0.05 }, "<")
          .to(text, { alpha: 1, duration: 0.1 }, "<");
      });
    }

    /**
     * Handle orientation change
     * @param {boolean} isLandscape
     */
    _orientationChange(isLandscape) {}
  }

  resourceManager.registerObjectType(
    "potMeters",
    () => new PotMeters(),
    () => {},
    () => []
  );
})();
